import React from "react";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";

import { useModalContext } from "../../contexts/ModalContext/context";
import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { Grid } from "../../UI/Grid";
import { SettingsOptionWrapper } from "./SettingsOptionWrapper";
import { Box } from "../../UI/Box";
import {
  COLOR_BLUE_BAYOUX,
  COLOR_IRON,
  COLOR_STALE_GRAY,
  COLOR_OCEAN_GREEN,
  COLOR_PACIFIC_BLUE,
  COLOR_PERSIMMON,
  COLOR_SUPERNOVA,
  COLOR_TRUE_V,
} from "../../constants/styles";
import { Switch } from "../Switch";
import {
  usePlacementWorksheetContext,
  usePlacementWorksheetSettingsContext,
} from "../../contexts/PlacementWorksheetContext";
import { putPlacementWorksheetSettings } from "../../services/PlacementWorksheetApi";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import { RoomLabel } from "../RoomLabel";

const COLORS = [
  COLOR_BLUE_BAYOUX,
  COLOR_STALE_GRAY,
  COLOR_IRON,
  COLOR_PACIFIC_BLUE,
  COLOR_OCEAN_GREEN,
  COLOR_TRUE_V,
  COLOR_SUPERNOVA,
  COLOR_PERSIMMON,
];

const StyledColorBox = styled(Box)<{ isSelected?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: ${(props) => Number(props.theme.shape.borderRadius) * 3}px;
  position: relative;
  cursor: pointer;

  &:after {
    content: ${(props) => (props.isSelected ? "''" : "none")};
    position: absolute;
    top: -4px;
    width: 32px;
    height: 32px;
    border-radius: ${(props) => Number(props.theme.shape.borderRadius) * 4}px;
    left: -4px;
    border: 2px solid ${(props) => props.theme.palette.primary.main};
  }
`;

export const PlacementWorksheetSettingsModal = () => {
  const { setIsOpen } = useModalContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getAuthToken, isWriteAccess, isSuperAdminRead } = useAuth();
  const { l2Id, l3Id } = usePlacementWorksheetContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const {
    labels,
    colors,
    updateColor,
    updateRoomLabel,
  } = usePlacementWorksheetSettingsContext();

  const closeModal = () => setIsOpen(false);
  const stopLoading = () => setIsLoading(false);

  const handleColorUpdate = (label: string, value: string) => () => {
    if (isWriteAccess()) {
      updateColor(label, value);
    }
  };

  const handleLabelUpdate = (id: number) => (value: string) => {
    if (isWriteAccess()) {
      updateRoomLabel(id, value);
    }
  };

  const handleError = () => {
    showErrorSnackbar("An Error Occurred");
  };
  const handleSuccess = () => {
    showSuccessSnackbar("Successfully updated");
    closeModal();
  };

  const handleSave = async () => {
    if (!l2Id || !l3Id || !selectedNetworkId) return;

    setIsLoading(true);
    putPlacementWorksheetSettings(
      selectedNetworkId,
      l2Id,
      l3Id,
      { ...colors, labelsToUpdate: labels },
      await getAuthToken()
    )
      .then(handleSuccess)
      .catch(handleError)
      .finally(stopLoading);
  };

  return (
    <Modal
      maxWidth="sm"
      onClose={closeModal}
      title="Customize"
      actions={
        <>
          <Button
            appearance="subtle"
            onClick={closeModal}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSave}
            isLoading={isLoading}
            isDisabled={isSuperAdminRead()}
          >
            Save
          </Button>
        </>
      }
    >
      <Grid container spacing={2} p={3}>
        <Grid item xs={12}>
          <SettingsOptionWrapper title="New resident">
            <Stack direction="row" spacing={1}>
              {COLORS.map((color) => (
                <StyledColorBox
                  key={color}
                  bgcolor={color}
                  onClick={handleColorUpdate("newResidentColor", color)}
                  isSelected={
                    color.toLowerCase() ===
                    colors?.newResidentColor?.toLowerCase()
                  }
                />
              ))}
            </Stack>
          </SettingsOptionWrapper>
        </Grid>
        <Grid item xs={12}>
          <SettingsOptionWrapper title="Renewing resident">
            <Stack direction="row" spacing={1}>
              {COLORS.map((color) => (
                <StyledColorBox
                  key={color}
                  bgcolor={color}
                  onClick={handleColorUpdate("renewingResidentColor", color)}
                  isSelected={
                    color.toLowerCase() ===
                    colors?.renewingResidentColor?.toLowerCase()
                  }
                />
              ))}
            </Stack>
          </SettingsOptionWrapper>
        </Grid>
        {/* <Grid item xs={12}>
          <SettingsOptionWrapper title="Renewing transfer">
            <Stack direction="row" spacing={1}>
              {COLORS.map((color) => (
                <StyledColorBox
                  bgcolor={color}
                  isSelected={color.toLowerCase() === colors?.renewalTransferResidentColor?.toLowerCase()}
                  onClick={handleColorUpdate("renewalTransferResidentColor", color)}
                />
              ))}
            </Stack>
          </SettingsOptionWrapper>
        </Grid> */}
        <Grid item xs={12}>
          <Switch
            isDisabled
            label="Disabled colors"
            switchProps={{
              checked: false,
              onChange: () => {},
            }}
          />
        </Grid>
        <Grid item xs={12} pb={2}>
          <SettingsOptionWrapper title="Room labels">
            <Stack direction="row" spacing={1}>
              {(labels || []).map((item) => (
                <RoomLabel
                  key={item.id}
                  value={item.label}
                  onConfirm={handleLabelUpdate(item.id)}
                />
              ))}
            </Stack>
          </SettingsOptionWrapper>
        </Grid>
      </Grid>
    </Modal>
  );
};
